import { Typography, Table, Result } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { ITransaction } from 'api'
import { RightOutlined } from '@ant-design/icons'
import Container from 'components/Container'
import { useLocalization } from 'localization'
import { FC, useMemo } from 'react'
import { getColorByFlow, getTransactionFlowIcon } from '../TransactionsPageContent/helpers'
import { useScreenMatch } from 'theme/hooks'

interface ITransactionsTable {
	transactions: ITransaction[]
	onSelect: (trx: ITransaction) => void
}

const TransactionsTable: FC<ITransactionsTable> = ({ transactions, onSelect }) => {
	const { t, f } = useLocalization()
	const match = useScreenMatch()

	const columns: ColumnsType<ITransaction> = useMemo(() => {
		let results: ColumnsType<ITransaction> = []

		if (match.gte('md')) {
			results = results.concat([
				{
					key: 'date',
					ellipsis: true,
					render: (_, { date }) => <>{f(date, { date: 'short', time: 'short' })}</>,
				},
				{
					key: 'type',
					render: (_, { type, flowType }) => (
						<Container display="flex" alignItems="center" space="sm">
							<Typography.Text type={getColorByFlow(flowType)}>{getTransactionFlowIcon(flowType)}</Typography.Text>
							<Typography.Text ellipsis title={t(`transactions.types.${type}`)}>
								{t(`transactions.types.${type}`)}
							</Typography.Text>
						</Container>
					),
				},
				{
					key: 'inOut',
					render: (_, { out, in: _in }) => {
						if (out?.amount) {
							return (
								<Container display="flex" alignItems="center" space="sm">
									<Typography.Text
										ellipsis
										title={`${out?.amount} ${out?.currency}`}
									>{`${out?.amount} ${out?.currency}`}</Typography.Text>
								</Container>
							)
						}

						if (_in?.amount) {
							return (
								<Container display="flex" alignItems="center" space="sm">
									<Typography.Text
										ellipsis
										title={`${_in?.amount} ${_in?.currency}`}
									>{`${_in?.amount} ${_in?.currency}`}</Typography.Text>
								</Container>
							)
						}

						return null
					},
				},
			])
		} else {
			results = results.concat([
				{
					key: 'type',
					ellipsis: true,
					render: (_, { type, date, out, in: _in, flowType }) => (
						<Container display="flex" flexDirection="column" space="xxs" spaceDirection="vertical">
							<Typography.Text>{f(date, { date: 'short', time: 'short' })}</Typography.Text>
							<Container display="flex" alignItems="center" space="xxs">
								<Typography.Text type={getColorByFlow(flowType)}>{getTransactionFlowIcon(flowType)}</Typography.Text>
								<Typography.Text ellipsis title={t(`transactions.types.${type}`)}>
									{t(`transactions.types.${type}`)}
								</Typography.Text>
							</Container>
							{out?.amount && (
								<Container display="flex" alignItems="center" space="sm">
									<Typography.Text
										ellipsis
										title={`${out?.amount} ${out?.currency}`}
									>{`${out?.amount} ${out?.currency}`}</Typography.Text>
								</Container>
							)}
							{_in?.amount && (
								<Container display="flex" alignItems="center" space="sm">
									<Typography.Text
										ellipsis
										title={`${_in?.amount} ${_in?.currency}`}
									>{`${_in?.amount} ${_in?.currency}`}</Typography.Text>
								</Container>
							)}
						</Container>
					),
				},
			])
		}

		results = results.concat([
			{
				key: 'action',
				align: 'right',
				width: 60,
				render: (_, trx) => (
					<Typography.Link
						onClick={e => {
							e.preventDefault()
							onSelect(trx)
						}}
					>
						<RightOutlined />
					</Typography.Link>
				),
			},
		])

		return results
	}, [f, t, match])

	return (
		<Table<ITransaction>
			size="middle"
			columns={columns}
			dataSource={transactions.map(trx => ({ ...trx, key: trx.id }))}
			showHeader={false}
			pagination={false}
			locale={{
				emptyText: (
					<Result
						title={t('transactions.createTransferModal.emptyState.title')}
						subTitle={t('transactions.createTransferModal.noMatches.subtitle')}
					/>
				),
			}}
		/>
	)
}

export default TransactionsTable
